import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'sm' | 'md';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-elevated text-muted border-border',
  accent: 'bg-accent-soft text-accent border-accent/30',
  success: 'bg-success-soft text-success border-success/30',
  warning: 'bg-warning-soft text-warning border-warning/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
  info: 'bg-info-soft text-info border-info/30',
};

const SIZES: Record<BadgeSize, string> = {
  sm: 'gap-1 px-1.5 py-px text-[10px]',
  md: 'gap-1.5 px-2 py-0.5 text-xs',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: BadgeSize;
  /** Leading status dot in the current tone. */
  dot?: boolean;
  /** Pulsing dot for in-flight states (publishing, analyzing). */
  pulse?: boolean;
}

export function Badge({
  className,
  tone = 'neutral',
  size = 'md',
  dot,
  pulse,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium leading-5 whitespace-nowrap',
        TONES[tone],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {(dot || pulse) && (
        <span className="relative flex size-1.5 shrink-0" aria-hidden>
          {pulse && (
            <span className="absolute inset-0 rounded-full bg-current animate-[var(--animate-pulse-ring)]" />
          )}
          <span className="relative size-1.5 rounded-full bg-current" />
        </span>
      )}
      {children}
    </span>
  );
}
